import { EXTENDED_DATA_SCHEMA_TYPES } from './types';
import { getFieldValue } from './fieldHelpers';

/**
 * Get the namespaced attribute key based on the specified extended data scope and attribute key
 * @param {*} scope extended data scope
 * @param {*} key attribute key in extended data
 * @returns a string containing the namespace prefix and the attribute name
 */
export const addScopePrefix = (scope, key) => {
  const scopeFnMap = {
    private: k => `priv_${k}`,
    protected: k => `prot_${k}`,
    public: k => `pub_${k}`,
    meta: k => `meta_${k}`,
  };

  const validKey = key.replace(/\s/g, '_');
  const keyScoper = scopeFnMap[scope];

  return !!keyScoper ? keyScoper(validKey) : validKey;
};

/**
 * Pick extended data fields from given form data.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This expects submit data to be namespaced (e.g. 'pub_') and it returns the field without that namespace.
 * This function is used when form submit values are restructured for the actual API endpoint.
 *
 * Note: This returns null for those fields that are managed by configuration, but don't match target user type.
 *       These might exists if user swaps between user types before saving the user.
 *
 * @param {Object} data values to look through against userConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetUserType Check that the extended data is relevant for this user type.
 * @param {Object} userFieldConfigs Extended data configurations for user fields.
 * @returns Array of picked extended data fields from submitted data.
 */
export const pickUserFieldsData = (data, targetScope, targetUserType, userFieldConfigs) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig.limitToUserTypeIds || userTypeConfig.userTypeIds.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, namespacedKey);
      return { ...fields, [key]: fieldValue };
    } else if (isKnownSchemaType && isTargetScope && !isTargetUserType) {
      // Note: this clears extra custom fields
      // These might exists if user swaps between user types before saving the user.
      return { ...fields, [key]: null };
    }
    return fields;
  }, {});
};

/**
 * Pick extended data fields from given extended data of the user entity.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This returns namespaced (e.g. 'pub_') initial values for the form.
 *
 * @param {Object} data extended data values to look through against userConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetUserType Check that the extended data is relevant for this user type.
 * @param {Object} userFieldConfigs Extended data configurations for user fields.
 * @returns Array of picked extended data fields
 */
export const initialValuesForUserFields = (data, targetScope, targetUserType, userFieldConfigs) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, key);
      return { ...fields, [namespacedKey]: fieldValue };
    }
    return fields;
  }, {});
};

/**
 * Returns props for custom user fields
 * @param {*} userFieldsConfig Configuration for user fields
 * @param {*} userType User type to restrict fields to. If none is passed,
 * only user fields applying to all user types are returned.
 * @param {*} isSignup Optional flag to determine whether the target context
 * is a signup form. Defaults to true.
 * @returns an array of props for CustomExtendedDataField: key, name,
 * fieldConfig, defaultRequiredMessage
 */
export const getPropsForCustomUserFieldInputs = (
  userFieldsConfig,
  userType = null,
  isSignup = true
) => {
  return (
    userFieldsConfig?.reduce((pickedFields, fieldConfig) => {
      const { key, userTypeConfig, schemaType, scope, saveConfig = {} } = fieldConfig || {};
      const namespacedKey = addScopePrefix(scope, key);
      const showField = isSignup ? saveConfig.displayInSignUp : true;

      const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
      const isTargetUserType =
        !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(userType);
      const isUserScope = ['public', 'private', 'protected'].includes(scope);

      return isKnownSchemaType && isTargetUserType && isUserScope && showField
        ? [
            ...pickedFields,
            {
              key: namespacedKey,
              name: namespacedKey,
              fieldConfig: fieldConfig,
            },
          ]
        : pickedFields;
    }, []) || []
  );
};

/**
 * Check if currentUser has permission to post listings.
 * Defined in currentUser's effectivePermissionSet relationship:
 * https://www.sharetribe.com/api-reference/marketplace.html#currentuser-permissionset
 *
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if currentUser has permission to post listings.
 */
export const hasPermissionToPostListings = currentUser => {
  if (currentUser?.id && !currentUser?.effectivePermissionSet?.id) {
    console.warn(
      '"effectivePermissionSet" relationship is not defined or included to the fetched currentUser entity.'
    );
  }
  return currentUser?.effectivePermissionSet?.attributes?.postListings === 'permission/allow';
};

/**
 * Check if currentUser has permission to initiate transactions.
 * Defined in currentUser's effectivePermissionSet relationship:
 * https://www.sharetribe.com/api-reference/marketplace.html#currentuser-permissionset
 *
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if currentUser has permission to initiate transactions.
 */
export const hasPermissionToInitiateTransactions = currentUser => {
  if (currentUser?.id && !currentUser?.effectivePermissionSet?.id) {
    console.warn(
      '"effectivePermissionSet" relationship is not defined or included to the fetched currentUser entity.'
    );
  }
  return (
    currentUser?.effectivePermissionSet?.attributes?.initiateTransactions === 'permission/allow'
  );
};

/**
 * Check if currentUser has permission to view listing and user data on a private marketplace.
 * Defined in currentUser's effectivePermissionSet relationship:
 * https://www.sharetribe.com/api-reference/marketplace.html#currentuser-permissionset
 *
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if currentUser has permission to view listing and user data on a private marketplace.
 */
export const hasPermissionToViewData = currentUser => {
  if (currentUser?.id && !currentUser?.effectivePermissionSet?.id) {
    console.warn(
      '"effectivePermissionSet" relationship is not defined or included to the fetched currentUser entity.'
    );
  }
  return currentUser?.effectivePermissionSet?.attributes?.read === 'permission/allow';
};

/**
 * Check if currentUser has been approved to gain access.
 * I.e. they are not in 'pending-approval' or 'banned' state.
 *
 * If the user is in 'pending-approval' state, they don't have right to post listings and initiate transactions.
 *
 * @param {Object} currentUser API entity.
 * @returns {Boolean} true if currentUser has been approved (state is 'active').
 */
export const isUserAuthorized = currentUser => currentUser?.attributes?.state === 'active';

/**
 * Get the user type configuration for the current user's user type
 * @param {*} config marketplace configuration
 * @param {*} currentUser API entity
 * @returns a single user type configuration, if found
 */
const getCurrentUserTypeConfig = (config, currentUser) => {
  const { userTypes } = config.user;
  return userTypes.find(
    ut => ut.userType === currentUser?.attributes?.profile?.publicData?.userType
  );
};

/**
 * Resolve the user's marketplace account type id the same way as AccountTypePage
 * and the account-type API (unknown / missing Console ids fall back to Hobbyist).
 *
 * @param {Object} config Marketplace configuration
 * @param {Object} currentUser API entity
 * @returns {string}
 */
export const getEffectiveStoredUserType = (config, currentUser) => {
  const HOBBYIST_ID = 'Hobbyist';
  const rawStoredType = currentUser?.attributes?.profile?.publicData?.userType;
  const userTypes = config?.user?.userTypes || [];
  const storedUserTypeMatchesConfig =
    !rawStoredType || userTypes.some(ut => ut.userType === rawStoredType);
  return rawStoredType && storedUserTypeMatchesConfig ? rawStoredType : HOBBYIST_ID;
};

/**
 * Roles for a Marketplace user-type id resolved from Console asset (`config.user.userTypes`).
 * R2R Hobbyist is always buy-only, overriding misconfigured Console roles.
 *
 * @param {string|null|undefined} userTypeId Id from `/users/user-types.json`
 * @param {*} config Marketplace configuration
 * @returns {{ customer: boolean, provider: boolean }}
 */
export const getUserTypeRolesFor = (userTypeId, config) => {
  const HOBBYIST_ID = 'Hobbyist';

  if (userTypeId === HOBBYIST_ID) {
    return { customer: true, provider: false };
  }

  const { userTypes = [] } = config.user || {};
  const cfg = userTypes.find(ut => ut.userType === userTypeId);
  if (cfg?.roles && typeof cfg.roles === 'object') {
    return {
      customer: cfg.roles.customer !== false,
      provider: !!cfg.roles.provider,
    };
  }
  return { customer: true, provider: true };
};

/**
 * True when the user's marketplace account type is buy-only (no provider/seller role).
 *
 * @param {Object} config Marketplace configuration
 * @param {Object} currentUser API entity
 * @returns {boolean}
 */
export const isBuyOnlyAccountType = (config, currentUser) => {
  if (!currentUser?.id) {
    return false;
  }
  const effectiveUserTypeId = getEffectiveStoredUserType(config, currentUser);
  const roles = getUserTypeRolesFor(effectiveUserTypeId, config);
  return roles.provider === false;
};

/**
 * Check if payout details tab and payout methods tab should be shown for the user
 * @param {Object} config Marketplace configuration
 * @param {*} currentUser API entity
 * @returns {Object} { showPayoutDetails: Boolean, showPaymentMethods: boolean }
 */
export const showPaymentDetailsForUser = (config, currentUser) => {
  if (isBuyOnlyAccountType(config, currentUser)) {
    return {
      showPayoutDetails: false,
      showPaymentMethods: false,
    };
  }

  const currentUserTypeConfig = getCurrentUserTypeConfig(config, currentUser);
  const { paymentMethods = true, payoutDetails = true } =
    currentUserTypeConfig?.accountLinksVisibility || {};

  return currentUser
    ? {
        showPayoutDetails: payoutDetails,
        showPaymentMethods: paymentMethods,
      }
    : {
        showPayoutDetails: false,
        showPaymentMethods: false,
      };
};

const getStripeAccountDataFromEntity = stripeAccount =>
  stripeAccount?.attributes?.stripeAccountData ?? null;

/** True when Stripe reports past_due / currently_due requirements (same heuristic as StripePayoutPage). */
const stripeAccountHasOutstandingRequirements = stripeAccountData => {
  if (stripeAccountData == null || !stripeAccountData.requirements) return false;
  const { past_due = [], currently_due = [] } = stripeAccountData.requirements;
  const has = arr => Array.isArray(arr) && arr.length > 0;
  return has(past_due) || has(currently_due);
};

/**
 * True when Connect payout onboarding isn’t satisfied (missing account or incomplete requirements).
 *
 * @param {Object|null|undefined} stripeAccount Marketplace `stripeAccount` entity or null
 */
export const stripeConnectPayoutIncomplete = stripeAccount => {
  if (!stripeAccount?.id) return true;
  const data = getStripeAccountDataFromEntity(stripeAccount);
  if (data == null) return true;
  return stripeAccountHasOutstandingRequirements(data);
};

/**
 * Seller user type expects payout details (`accountLinksVisibility.payoutDetails`), but Stripe isn’t finished.
 *
 * Pass `stripeAccountOverride` when Redux carries a fresher `stripeAccount` than `currentUser.stripeAccount`.
 */
export const sellerStripePayoutSetupIncomplete = (config, currentUser, stripeAccountOverride) => {
  if (!currentUser) return false;
  const { showPayoutDetails } = showPaymentDetailsForUser(config, currentUser);
  if (!showPayoutDetails) return false;
  const stripeAccount =
    stripeAccountOverride !== undefined ? stripeAccountOverride : currentUser?.stripeAccount;
  return stripeConnectPayoutIncomplete(stripeAccount);
};

/**
 * Check the roles defined for the current user
 * @param {*} config Marketplace configuration
 * @param {*} currentUser API entity
 * @returns Object with attributes 'customer' and 'provider' and boolean values for each
 */
export const getCurrentUserTypeRoles = (config, currentUser) => {
  const effectiveUserTypeId = getEffectiveStoredUserType(config, currentUser);
  return getUserTypeRolesFor(effectiveUserTypeId, config);
};

/**
 * Check if the links for creating a new listing should be shown to the
 * user currently browsing the marketplace.
 *
 * Logged-in users always see the create-listing entry so buy-only accounts can
 * open the flow and read upgrade guidance on the listing wizard page.
 *
 * @param {Object} config Marketplace configuration
 * @param {Object} currentUser API entity
 * @returns {Boolean} true if the currentUser's user type, or the anonymous user configuration, is set to see the link
 */
export const showCreateListingLinkForUser = (config, currentUser) => {
  if (currentUser?.id) {
    return true;
  }

  const { topbar } = config;
  const currentUserTypeConfig = getCurrentUserTypeConfig(config, currentUser);

  const { accountLinksVisibility } = currentUserTypeConfig || {};

  return accountLinksVisibility
    ? accountLinksVisibility.postListings
    : topbar?.postListingsLink
    ? topbar.postListingsLink.showToUnauthenticatedUsers
    : true;
};

/** True when `targetUserTypeId` disables provider role while current type allows it. */
export const userTypeSwitchRemovesProviderRole = (currentUserTypeId, targetUserTypeId, config) => {
  const currentRoles = getUserTypeRolesFor(currentUserTypeId, config);
  const targetRoles = getUserTypeRolesFor(targetUserTypeId, config);
  return currentRoles.provider === true && targetRoles.provider === false;
};

/** True when `targetUserTypeId` enables provider role while current type does not. */
export const userTypeSwitchAddsProviderRole = (currentUserTypeId, targetUserTypeId, config) => {
  const currentRoles = getUserTypeRolesFor(currentUserTypeId, config);
  const targetRoles = getUserTypeRolesFor(targetUserTypeId, config);
  return currentRoles.provider === false && targetRoles.provider === true;
};

/** Trailing Console/marketing fee parenthetical, e.g. " (5% marketplace fee)". */
const MARKETPLACE_FEE_PARENTHETICAL_RE = /\s*\(\d+(?:\.\d+)?%\s*[^)]*\)\s*$/i;

/**
 * Strip a trailing fee parenthetical from Account Type labels (UI does not show commission %).
 * Preserves other parentheticals such as "(Buy only)".
 *
 * @param {string} label
 * @returns {string}
 */
export const stripMarketplaceFeeParenthetical = label =>
  String(label || '')
    .replace(MARKETPLACE_FEE_PARENTHETICAL_RE, '')
    .trim();

/**
 * Shorten Console account-type labels for CTA copy by stripping parentheticals.
 * e.g. "Hobbyist (Buy only)" → "Hobbyist"
 *
 * Prefer the full fee-stripped label when buy-only and sell Hobbyist would collide.
 *
 * @param {string} label
 * @returns {string}
 */
export const shortAccountTypeLabel = label => {
  const stripped = String(label || '')
    .replace(/\s*\([^)]*\)/g, '')
    .trim();
  return stripped || label;
};

/** Sharetribe id for R2R Sponsor. */
export const COMMERCIAL_SPONSOR_USER_TYPE = 'commercial_sponsor';
/** Sharetribe id for R2R Supporter. */
export const R2R_SUPPORTER_USER_TYPE = 'supporting_hobbyist_buy_sell';

/**
 * Whether to show the XenForo forum-upgrade CTA on Account Type, and which note copy.
 * Visibility is based on current type + switchable type ids (what the user can already access).
 *
 * @param {Object} params
 * @param {string|null|undefined} params.currentUserTypeId
 * @param {string[]} [params.switchableTypeIds]
 * @param {boolean} [params.switchableTypesInProgress]
 * @returns {{ show: boolean, noteId: string|null }}
 */
export const getForumUpgradeCtaState = ({
  currentUserTypeId,
  switchableTypeIds = [],
  switchableTypesInProgress = false,
}) => {
  if (switchableTypesInProgress) {
    return { show: false, noteId: null };
  }

  const visible = new Set(
    [currentUserTypeId, ...(Array.isArray(switchableTypeIds) ? switchableTypeIds : [])].filter(
      id => typeof id === 'string' && id.trim() !== ''
    )
  );
  const canSeeCommercialSponsor = visible.has(COMMERCIAL_SPONSOR_USER_TYPE);
  const canSeeR2rSupporter = visible.has(R2R_SUPPORTER_USER_TYPE);

  if (canSeeCommercialSponsor && canSeeR2rSupporter) {
    return { show: false, noteId: null };
  }

  const noteId = canSeeCommercialSponsor
    ? 'AccountTypePage.forumUpgradeNoteSupporter'
    : canSeeR2rSupporter
    ? 'AccountTypePage.forumUpgradeNoteSponsor'
    : 'AccountTypePage.forumUpgradeNoteBoth';

  return { show: true, noteId };
};
